import {
  GrowthKpiTelemetry,
  AudienceSegment,
  CampaignJourney,
  CommunicationTemplateItem,
  EmailDeliverabilityStats,
  ReferralProgramTelemetry,
  CustomerSupportTicket,
  CrmUserProfile,
  DeliverableP12
} from '../types';

export const INITIAL_GROWTH_TELEMETRY: GrowthKpiTelemetry = {
  newQualifiedVisitors30d: 584000,
  firstUsefulCalculationActivationPct: 78.4,
  accountActivationPct: 26.4,
  d30RetentionPct: 24.8,
  reactivationRatePct: 18.2,
  savedCalculationAdoptionPct: 21.5,
  alertAdoptionPct: 14.8,
  activeReferrersCount: 4120,
  qualifiedReferralsCount: 1850,
  unsubscribeRatePct: 0.12, // Very low (<0.5% industry standard)
  spamComplaintRatePct: 0.01, // Well below 0.1% Gmail/Yahoo threshold
  emailDeliveryRatePct: 99.4,
  pushOptInRatePct: 18.6,
  activeAutomatedJourneysCount: 5,
  criticalGrowthIncidentsCount: 0,
  suppressionListCount: 312,
};

export const INITIAL_AUDIENCE_SEGMENTS: AudienceSegment[] = [
  {
    id: 'seg_01',
    segmentKey: 'seg_salary_tool_power_users',
    name: 'Salary Calculator Active Users',
    description: 'Users who executed at least 1 paycheck tax calculation in the past 30 days without financial value profiling.',
    privacyClassification: 'NON_SENSITIVE_BEHAVIORAL',
    rulesCount: 3,
    estimatedAudienceSize: 142000,
    refreshFrequency: 'HOURLY',
    privacyReviewPassed: true,
    status: 'ACTIVE',
    updatedAt: '2026-08-24T12:00:00Z',
  },
  {
    id: 'seg_02',
    segmentKey: 'seg_fx_rate_alert_subscribers',
    name: 'Currency & Exchange Rate Alert Subscribers',
    description: 'Users with at least 1 active exchange rate monitoring alert.',
    privacyClassification: 'NON_SENSITIVE_BEHAVIORAL',
    rulesCount: 2,
    estimatedAudienceSize: 48500,
    refreshFrequency: 'REALTIME',
    privacyReviewPassed: true,
    status: 'ACTIVE',
    updatedAt: '2026-08-24T14:30:00Z',
  },
  {
    id: 'seg_03',
    segmentKey: 'seg_dormant_30d_tax_users',
    name: 'Dormant 30-Day Paycheck Users (Tax Update Candidates)',
    description: 'Registered users with no meaningful calculation in 30 days, eligible for tax rule updates.',
    privacyClassification: 'LIFECYCLE_STAGE',
    rulesCount: 4,
    estimatedAudienceSize: 28400,
    refreshFrequency: 'DAILY',
    privacyReviewPassed: true,
    status: 'ACTIVE',
    updatedAt: '2026-08-24T06:00:00Z',
  },
  {
    id: 'seg_04',
    segmentKey: 'seg_transfer_corridor_favorited',
    name: 'Remittance & Transfer Corridor Followers',
    description: 'Users following specific remittance corridors (e.g. US->IN, CA->PH, US->MX).',
    privacyClassification: 'NON_SENSITIVE_BEHAVIORAL',
    rulesCount: 2,
    estimatedAudienceSize: 62100,
    refreshFrequency: 'HOURLY',
    privacyReviewPassed: true,
    status: 'ACTIVE',
    updatedAt: '2026-08-24T11:15:00Z',
  },
];

export const INITIAL_CAMPAIGN_JOURNEYS: CampaignJourney[] = [
  {
    id: 'jny_01',
    campaignKey: 'jny_account_activation_onboarding',
    name: 'Account Registration & Contextual Value Journey',
    objective: 'Guide newly registered users to save their first financial scenario or create an alert.',
    category: 'PRODUCT_LIFECYCLE',
    targetSegmentName: 'Newly Registered Accounts (0-7 Days)',
    channels: ['IN_APP', 'EMAIL'],
    frequencyCapRule: 'Max 1 message per 7 days',
    consentRequiredCategory: 'productUpdates',
    status: 'RUNNING',
    journeyNodes: [
      { stepNumber: 1, type: 'TRIGGER', label: 'Event: account_created', details: 'Triggers immediately upon Passkey/MagicLink registration' },
      { stepNumber: 2, type: 'CONDITION_CHECK', label: 'Check: Has Saved Scenario?', details: 'If YES, branch to In-App confirmation; If NO, wait 24h' },
      { stepNumber: 3, type: 'WAIT', label: 'Wait 24 Hours', details: 'Allows organic session completion' },
      { stepNumber: 4, type: 'CONSENT_CHECK', label: 'Consent & Suppression Guard', details: 'Verifies productUpdates consent and suppression list' },
      { stepNumber: 5, type: 'ACTION_SEND', label: 'Send Lifecycle Email', details: 'Subject: "Your YamooPay saved calculations are ready"' },
    ],
    metrics: {
      eligibleUsers: 14200,
      suppressedUsersCount: 120,
      sentCount: 14080,
      deliveredCount: 14010,
      clickedCount: 4210,
      activatedUsersCount: 3120,
      unsubscribedCount: 14,
      spamComplaintsCount: 0,
    },
    createdAt: '2026-08-10',
  },
  {
    id: 'jny_02',
    campaignKey: 'jny_tax_year_update_reactivation',
    name: 'Annual Tax Rules & Payroll Update Reactivation',
    objective: 'Notify dormant paycheck users when new state/provincial tax rules are published.',
    category: 'PRODUCT_LIFECYCLE',
    targetSegmentName: 'Dormant 30-Day Paycheck Users',
    channels: ['EMAIL', 'PUSH'],
    frequencyCapRule: 'Max 1 message per 30 days',
    consentRequiredCategory: 'productUpdates',
    status: 'RUNNING',
    journeyNodes: [
      { stepNumber: 1, type: 'TRIGGER', label: 'Event: tax_rules_updated', details: 'Triggers when a verified TaxRuleSet is updated for user market' },
      { stepNumber: 2, type: 'FREQUENCY_CAP_CHECK', label: 'Frequency & Quiet Hours Check', details: 'Ensures no message sent within quiet hours' },
      { stepNumber: 3, type: 'ACTION_SEND', label: 'Send Reactivation Alert', details: 'Subject: "New tax rules published for your saved scenario"' },
    ],
    metrics: {
      eligibleUsers: 28400,
      suppressedUsersCount: 340,
      sentCount: 28060,
      deliveredCount: 27920,
      clickedCount: 8450,
      activatedUsersCount: 5180,
      unsubscribedCount: 32,
      spamComplaintsCount: 1,
    },
    createdAt: '2026-08-15',
  },
];

export const INITIAL_COMMUNICATION_TEMPLATES: CommunicationTemplateItem[] = [
  {
    id: 'tmpl_01',
    templateKey: 'tmpl_fx_target_alert_push',
    category: 'FINANCIAL_ALERT',
    locale: 'en-US',
    subject: 'Exchange Rate Alert: {{currencyPair}} target reached',
    preheader: 'Your watched exchange rate target has been reached on YamooPay.',
    variablesList: ['currencyPair', 'targetRate', 'marketName'], // NO EXACT PRIVATE PORTFOLIO AMOUNTS!
    status: 'APPROVED',
    lastReviewedBy: 'Compliance & Growth Officer',
  },
  {
    id: 'tmpl_02',
    templateKey: 'tmpl_tax_year_update_email',
    category: 'PRODUCT_LIFECYCLE',
    locale: 'en-US',
    subject: 'Updated {{jurisdictionName}} tax rules are now available',
    preheader: 'Recalculate your saved paycheck scenario with the latest tax tables.',
    variablesList: ['jurisdictionName', 'taxYear'],
    status: 'APPROVED',
    lastReviewedBy: 'Head of Product Marketing',
  },
];

export const INITIAL_EMAIL_DELIVERABILITY: EmailDeliverabilityStats = {
  providerName: 'YamooPay Multi-Provider SMTP / API Adapter (Transactional & Lifecycle Stream Isolation)',
  spfAuthenticated: true,
  dkimAuthenticated: true,
  dmarcPolicy: 'reject', // Strictest DMARC protection p=reject
  sent24h: 42800,
  deliveryRatePct: 99.4,
  bounceRatePct: 0.58,
  complaintRatePct: 0.01,
  unsubscribeRatePct: 0.12,
  suppressionListCount: 312,
  healthStatus: 'OPTIMAL',
};

export const INITIAL_REFERRAL_TELEMETRY: ReferralProgramTelemetry = {
  programName: 'YamooPay Authenticated Peer Advocacy Program',
  activeReferrersCount: 4120,
  totalInvitesSentCount: 12400,
  totalQualifiedReferralsCount: 1850,
  conversionRatePct: 14.9,
  fraudBlockedAttemptsCount: 142,
  rewardType: 'FEATURE_UNLOCK',
  estimatedLiabilityUsd: 0, // Non-cash feature unlocks eliminate cash liabilities!
  status: 'ACTIVE_TIERED',
};

export const INITIAL_SUPPORT_TICKETS: CustomerSupportTicket[] = [
  {
    id: 'tkt_801',
    ticketNumber: 'SUP-2026-801',
    category: 'CALCULATION',
    priority: 'NORMAL',
    toolContext: 'Salary Calculator (CA-ON)',
    userEmailMasked: 'j***@example.com',
    messagePreview: 'Question regarding CPP2 deduction rules in 2026 Ontario paycheck tax breakdown.',
    userFinancialDataAttached: false, // MANDATORY GUARANTEE!
    status: 'IN_PROGRESS',
    createdAt: '2026-08-24T10:14:00Z',
  },
  {
    id: 'tkt_802',
    ticketNumber: 'SUP-2026-802',
    category: 'PRIVACY',
    priority: 'HIGH',
    toolContext: 'Privacy & Preference Center',
    userEmailMasked: 'm***@domain.org',
    messagePreview: 'Requesting confirmation of complete marketing email opt-out status.',
    userFinancialDataAttached: false,
    status: 'RESOLVED',
    createdAt: '2026-08-23T16:30:00Z',
  },
];

export const INITIAL_CRM_USER_SAMPLE: CrmUserProfile = {
  id: 'crm_usr_9814',
  userId: 'usr_849102',
  anonymousId: 'anon_sys_482910',
  lifecycleStage: 'ACCOUNT_ACTIVATED',
  locale: 'en-US',
  marketPreference: 'US',
  timezone: 'America/New_York',
  firstSeenAt: '2026-08-01T09:00:00Z',
  lastMeaningfulActivityAt: '2026-08-24T14:20:00Z',
  activatedAt: '2026-08-01T09:12:00Z',
  retainedStatus: true,
  dormantDays: 0,
  consentStatus: {
    securityEssential: true,
    financialAlerts: true,
    productUpdates: true,
    marketingConsent: false, // Opted out of promotional marketing!
  },
  notificationPreferences: {
    emailFrequency: 'WEEKLY',
    pushEnabled: true,
    quietHoursActive: fontQuietHoursActive(),
  },
  productUsageTags: ['SALARY_TOOL', 'FX_ALERT_ACTIVE', 'MORTGAGE_SAVED'],
};

function fontQuietHoursActive(): boolean {
  return true;
}

export const DELIVERABLES_P12: DeliverableP12[] = [
  {
    code: 'DEL-P12-01',
    titleEn: 'CRM Core & User Lifecycle Engine',
    titleFr: 'Cœur CRM & Moteur de Cycle de Vie Utilisateur',
    category: 'CRM Core & User Lifecycle Engine',
    summary: 'Centralized non-sensitive CRM engine tracking 10 distinct user lifecycle stages (Visitor -> Engaged -> Activated -> Account Activated -> Retained -> Dormant -> Reactivated -> Advocate).',
    details: [
      'Strict separation between Product Lifecycle State and Marketing Permission (retained users remain retained even if opted out of marketing).',
      'Event-driven transitions consuming analytics events without direct React component coupling.',
      'Strict rule: Zero raw financial values (salaries, debts, loan balances) stored in CRM profiles.',
      'Quiet Hours and Frequency Cap governance preventing user communication fatigue.',
    ],
    architectureNotes: 'YamooPay CRM profile references the User model without duplicating account databases.',
  },
  {
    code: 'DEL-P12-02',
    titleEn: 'Audience Segmentation & Privacy Governance',
    titleFr: 'Segmentation des Audiences & Gouvernance de Confidentialité',
    category: 'Audience Segmentation & Privacy Governance',
    summary: 'Dynamic audience segment builder categorizing users by non-sensitive behavioral tags, locale, market, and consent state.',
    details: [
      'Behavioral & product segments (e.g. Salary Users, FX Alert Subscribers, Remittance Followers).',
      'Strict Privacy Firewall: Segments based on exact salary or exact debt amounts are strictly PROHIBITED.',
      'Dynamic segment recalculation and snapshot auditing for major campaign dispatches.',
      'Automatic suppression enforcement filtering out unsubscribed or bounced addresses.',
    ],
    architectureNotes: 'Rules use coarse behavioral tags — never raw dollar inputs.',
  },
  {
    code: 'DEL-P12-03',
    titleEn: 'Campaign Orchestration & Journey Builder',
    titleFr: 'Orchestration des Campagnes & Moteur de Parcours',
    category: 'Campaign Orchestration & Journey Builder',
    summary: 'Multi-node visual journey builder supporting automated lifecycle playbooks (Account Activation, First Save, FX Alert, Tax Update, Dormant Reactivation).',
    details: [
      'Multi-channel orchestration across In-App notifications, Web Push, and Email.',
      'Safety checks per node: Consent Check, Suppression Check, Frequency Cap Check, Quiet Hours Check.',
      'Emergency Campaign Kill Switch allowing immediate global or channel-specific campaign pauses.',
      'Versioned journey definitions guaranteeing active campaigns run deterministically.',
    ],
    architectureNotes: 'Every campaign dispatch is idempotent with deduplication keys preventing double sends.',
  },
  {
    code: 'DEL-P12-04',
    titleEn: 'Email Deliverability, Templates & Preference Center',
    titleFr: 'Délivrabilité Email, Modèles & Centre de Préférences',
    category: 'Email Deliverability, Templates & Preference Center',
    summary: 'Email Provider Adapter with transactional/marketing stream isolation, SPF/DKIM/DMARC alignment, and granular Communication Preference Center.',
    details: [
      'Multi-provider SMTP/API abstraction isolating transactional security emails from promotional lifecycle campaigns.',
      'Strict DMARC policy (p=reject) with real-time bounce, complaint (<0.01%), and unsubscribe tracking.',
      'Centralized Suppression List ensuring unsubscribed users are never re-contacted.',
      'Granular My YamooPay Preference Center allowing category and channel-level controls.',
    ],
    architectureNotes: 'One-click unsubscribe readiness complying with modern deliverability standards.',
  },
  {
    code: 'DEL-P12-05',
    titleEn: 'Customer Success, Referral Program & Anti-Abuse',
    titleFr: 'Succès Client, Programme de Parrainage & Anti-Abus',
    category: 'Customer Success, Referral Program & Anti-Abuse',
    summary: 'Self-service Customer Success support desk and peer-to-peer referral engine backed by automated anti-fraud risk scoring.',
    details: [
      'Support Request Desk with context tracking without attaching private financial scenario inputs.',
      'Tiered referral program offering non-cash feature unlocks (eliminating financial liabilities).',
      'Referral Risk Engine detecting self-referral, disposable emails, and rapid account farming attempts.',
      'Explicit disclosure terms and one-click shareable tool templates (sharing tool templates, not private results).',
    ],
    architectureNotes: 'Referral conversions require qualified product calculation actions — simple pageviews do not count.',
  },
  {
    code: 'DEL-P12-06',
    titleEn: 'Growth Command Center & Executive Intelligence',
    titleFr: 'Centre de Commandement Growth & Intelligence Exécutive',
    category: 'Growth Command Center & Executive Intelligence',
    summary: 'Executive Growth Cockpit providing real-time visibility into Activation Rate, Retention, Email Deliverability, Referral Conversions, and Growth Opportunities.',
    details: [
      'Executive KPI Row: Qualified Visitors (584k), Activation Rate (78.4%), D30 Retention (24.8%), Reactivation Rate (18.2%), and Unsubscribe Rate (0.12%).',
      'Integrated sub-views: User Lifecycle CRM, Segment Builder, Journey Builder, Email Deliverability, Referral Program, and Customer Support.',
      'Growth Opportunity Engine identifying high-potential lifecycle and market expansion levers.',
      'Premium Light Mode visual language conforming strictly to YamooPay Design System standards.',
    ],
    architectureNotes: 'Unified decision center connecting acquisition value with long-term user retention.',
  },
];
